import type { Listing } from '@seller-portal/types';
import { Badge, Table, Td, Th, Tr } from '@/components/ui';
import { price, since, stock, variantText } from './format';
import { ProductCell } from './ProductCell';
import type { SelectionState } from './selection';

interface ListingsTableProps {
  sellerId: string;
  listings: Listing[];
  /** Whether any listing in view has a rating, which decides whether the column shows. */
  showRating: boolean;
  /** Row checkboxes; without it the table is read-only. */
  selection?: SelectionState;
}

export function listingPath(sellerId: string, id: string): string {
  return `/${sellerId}/products/${encodeURIComponent(id)}`;
}

/**
 * The Variants view of Products (spec S03, prototype "Variants"): one row per listing, with only
 * the columns that have data. Every listing the index serves is published, so it reads "Ready";
 * blocked listings are on Issues.
 */
export function ListingsTable({ sellerId, listings, showRating, selection }: ListingsTableProps) {
  const pageTicked =
    selection !== undefined &&
    listings.length > 0 &&
    listings.every((l) => selection.hasListing(l.id));
  return (
    <Table>
      <thead>
        <tr>
          {selection ? (
            <Th className="w-8">
              <input
                type="checkbox"
                aria-label="Select this page"
                checked={pageTicked}
                disabled={selection.all}
                onChange={(e) => selection.setListings(listings, e.target.checked)}
              />
            </Th>
          ) : null}
          <Th>Variant</Th>
          <Th>Ready</Th>
          <Th align="right">Price</Th>
          <Th align="right">Stock</Th>
          <Th align="right">First seen</Th>
          {showRating ? <Th align="right">Rating</Th> : null}
        </tr>
      </thead>
      <tbody>
        {listings.map((listing) => (
          <Tr key={listing.id} selected={selection?.hasListing(listing.id) ?? false}>
            {selection ? (
              <Td>
                <input
                  type="checkbox"
                  aria-label={`Select ${listing.itemCode}`}
                  checked={selection.hasListing(listing.id)}
                  disabled={selection.all}
                  onChange={() => selection.toggleListing(listing)}
                />
              </Td>
            ) : null}
            <Td>
              <ProductCell
                image={listing.image}
                title={[listing.title || listing.itemCode, variantText(listing)]
                  .filter(Boolean)
                  .join(' · ')}
                sub={listing.itemCode}
                mono
                to={listingPath(sellerId, listing.id)}
              />
            </Td>
            <Td>
              <Badge tone="good" dot>
                Ready
              </Badge>
            </Td>
            <Td align="right" className="whitespace-nowrap">
              {listing.priceHidden ? (
                <span className="text-muted">Hidden</span>
              ) : listing.regularPrice !== null &&
                listing.price !== null &&
                listing.regularPrice > listing.price ? (
                <>
                  <b>{price(listing.price, listing.currency)}</b>{' '}
                  <span className="text-faint text-[11px] line-through">
                    {price(listing.regularPrice, listing.currency)}
                  </span>
                </>
              ) : (
                price(listing.price, listing.currency)
              )}
            </Td>
            <Td align="right" className={`whitespace-nowrap ${listing.inStock ? '' : 'text-red'}`}>
              {stock(listing)}
            </Td>
            <Td align="right" className="text-muted whitespace-nowrap">
              {since(listing.firstSeenAt)}
            </Td>
            {showRating ? (
              <Td align="right" className="text-muted">
                {listing.rating !== null
                  ? `${listing.rating.toFixed(1)} ★ · ${listing.reviewCount ?? 0}`
                  : '—'}
              </Td>
            ) : null}
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}
